/** 印花区域参数选项与默认值（每个印花区域独立存储） */

/** 与商品设计 / POD 套图 ipelayer 图层名一致的默认印花区域名称 */
export function defaultStampAreaName(index = 1) {
  const n = Math.max(1, Math.round(Number(index) || 1));
  return `ipelayer${n}`;
}

export function nextDefaultStampAreaName(existingCount = 0) {
  return defaultStampAreaName(existingCount + 1);
}

export const STAMP_ASPECT_RATIO_OPTIONS = [
  { value: 'manual', label: '手动调整' },
  { value: '1:1', label: '1:1' },
  { value: '1:2', label: '1:2' },
  { value: '2:3', label: '2:3' },
  { value: '3:2', label: '3:2' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '16:9', label: '16:9' },
  { value: '9:16', label: '9:16' },
];

export const STAMP_FILL_MODE_OPTIONS = [
  { value: 'contain', label: '适应印花区域' },
  { value: 'cover', label: '充满印花区域' },
  { value: 'stretch', label: '拉伸至印花区域' },
];

export const STAMP_OBJECT_POSITION_OPTIONS = [
  { value: 'center center', label: '居中对齐' },
  { value: 'center top', label: '向上对齐' },
  { value: 'center bottom', label: '向下对齐' },
  { value: 'left center', label: '向左对齐' },
  { value: 'right center', label: '向右对齐' },
];

export const STAMP_ROTATION_OPTIONS = [
  { value: 0, label: '360°' },
  { value: 90, label: '90°' },
  { value: 180, label: '180°' },
  { value: 270, label: '270°' },
];

export const DEFAULT_STAMP_PARAMS = {
  aspectRatio: '1:1',
  fillMode: 'contain',
  objectPosition: 'center center',
  stroke: false,
  strokeWidth: 2,
  strokeColor: '#2563eb',
  stampRotation: 0,
  opacity: 100,
  brightness: 100,
  wrinkleFit: false,
  wrinkleIntensity: 50,
  ambientLight: false,
  highlightIntensity: 50,
  shadowIntensity: 80,
  shadow: false,
  shadowDiffusion: 85,
  shadowOffsetX: 20,
  shadowOffsetY: 20,
  shadowColor: '#666666',
};

function normalizeStampRotation(value) {
  const num = Number(value);
  if (num === 360 || num === 0) return 0;
  if ([90, 180, 270].includes(num)) return num;
  return DEFAULT_STAMP_PARAMS.stampRotation;
}

export function parseAspectRatioValue(value) {
  if (!value || value === 'manual') return null;
  const parts = String(value).split(':').map(Number);
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return parts[0] / parts[1];
}

export function guessAspectRatio(width, height) {
  if (!width || !height) return 'manual';
  const ratio = width / height;
  for (const opt of STAMP_ASPECT_RATIO_OPTIONS) {
    if (opt.value === 'manual') continue;
    const parsed = parseAspectRatioValue(opt.value);
    if (parsed && Math.abs(ratio - parsed) < 0.03) {
      return opt.value;
    }
  }
  return 'manual';
}

/** 按目标比例调整宽高，保持面积相近 */
export function resizeToAspectRatio(width, height, aspectRatio) {
  const target = parseAspectRatioValue(aspectRatio);
  if (!target || !width || !height) {
    return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
  }
  const area = width * height;
  const nextHeight = Math.sqrt(area / target);
  const nextWidth = nextHeight * target;
  return {
    width: Math.max(1, Math.round(nextWidth)),
    height: Math.max(1, Math.round(nextHeight)),
  };
}

function clampNumber(value, fallback, min = 0, max = 100) {
  const num = Number(value);
  if (Number.isNaN(num)) return fallback;
  return Math.min(max, Math.max(min, Math.round(num)));
}

function normalizeHexColor(value, fallback) {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    const r = raw[1];
    const g = raw[2];
    const b = raw[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

export function normalizeStampParams(raw = {}) {
  return {
    aspectRatio: raw.aspectRatio || DEFAULT_STAMP_PARAMS.aspectRatio,
    fillMode: raw.fillMode || DEFAULT_STAMP_PARAMS.fillMode,
    objectPosition: raw.objectPosition || DEFAULT_STAMP_PARAMS.objectPosition,
    stroke: Boolean(raw.stroke),
    strokeWidth: clampNumber(raw.strokeWidth, DEFAULT_STAMP_PARAMS.strokeWidth, 1, 20),
    strokeColor: normalizeHexColor(raw.strokeColor, DEFAULT_STAMP_PARAMS.strokeColor),
    stampRotation: normalizeStampRotation(raw.stampRotation),
    opacity: clampPercent(raw.opacity, DEFAULT_STAMP_PARAMS.opacity),
    brightness: clampPercent(raw.brightness, DEFAULT_STAMP_PARAMS.brightness, 200),
    wrinkleFit: Boolean(raw.wrinkleFit),
    wrinkleIntensity: clampNumber(raw.wrinkleIntensity, DEFAULT_STAMP_PARAMS.wrinkleIntensity),
    ambientLight: raw.ambientLight !== undefined
      ? Boolean(raw.ambientLight)
      : DEFAULT_STAMP_PARAMS.ambientLight,
    highlightIntensity: clampPercent(raw.highlightIntensity, DEFAULT_STAMP_PARAMS.highlightIntensity),
    shadowIntensity: clampPercent(raw.shadowIntensity, DEFAULT_STAMP_PARAMS.shadowIntensity),
    shadow: Boolean(raw.shadow),
    shadowDiffusion: clampNumber(raw.shadowDiffusion, DEFAULT_STAMP_PARAMS.shadowDiffusion),
    shadowOffsetX: clampNumber(raw.shadowOffsetX, DEFAULT_STAMP_PARAMS.shadowOffsetX, -100, 100),
    shadowOffsetY: clampNumber(raw.shadowOffsetY, DEFAULT_STAMP_PARAMS.shadowOffsetY, -100, 100),
    shadowColor: normalizeHexColor(raw.shadowColor, DEFAULT_STAMP_PARAMS.shadowColor),
  };
}

function clampPercent(value, fallback, max = 100) {
  const num = Number(value);
  if (Number.isNaN(num)) return fallback;
  return Math.min(max, Math.max(0, Math.round(num)));
}

export function stampParamsFromLinkData(linkData = {}) {
  return normalizeStampParams({
    aspectRatio: linkData.aspectRatio,
    fillMode: linkData.fillMode,
    objectPosition: linkData.objectPosition,
    stroke: linkData.stroke,
    strokeWidth: linkData.strokeWidth,
    strokeColor: linkData.strokeColor,
    stampRotation: linkData.stampRotation,
    opacity: linkData.opacity,
    brightness: linkData.brightness,
    wrinkleFit: linkData.wrinkleFit,
    wrinkleIntensity: linkData.wrinkleIntensity,
    ambientLight: linkData.ambientLight,
    highlightIntensity: linkData.highlightIntensity,
    shadowIntensity: linkData.shadowIntensity,
    shadow: linkData.shadow,
    shadowDiffusion: linkData.shadowDiffusion,
    shadowOffsetX: linkData.shadowOffsetX,
    shadowOffsetY: linkData.shadowOffsetY,
    shadowColor: linkData.shadowColor,
  });
}

export function mergeStampParamsIntoLinkData(linkData, params) {
  const normalized = normalizeStampParams(params);
  return {
    ...linkData,
    role: 'stampArea',
    ...normalized,
  };
}

export function stampParamsToExportFields(params) {
  return normalizeStampParams(params);
}

/** 效果面板参数（不含贴合/描边等） */
export const STAMP_EFFECT_PARAM_KEYS = [
  'opacity',
  'brightness',
  'wrinkleFit',
  'wrinkleIntensity',
  'shadow',
  'shadowDiffusion',
  'shadowOffsetX',
  'shadowOffsetY',
  'shadowColor',
];

export function pickStampEffectParams(params) {
  const normalized = normalizeStampParams(params);
  return STAMP_EFFECT_PARAM_KEYS.reduce((acc, key) => {
    acc[key] = normalized[key];
    return acc;
  }, {});
}

export function mergeStampEffectParamsIntoLinkData(linkData, effectParams) {
  return mergeStampParamsIntoLinkData(linkData, {
    ...stampParamsFromLinkData(linkData),
    ...pickStampEffectParams(effectParams),
  });
}

export function stampExampleFromLinkData(linkData = {}) {
  if (!linkData.exampleImageUrl) return {};
  return {
    exampleImageUrl: linkData.exampleImageUrl,
    exampleImageId: linkData.exampleImageId || undefined,
    exampleImageName: linkData.exampleImageName || undefined,
    exampleSource: linkData.exampleSource || undefined,
  };
}

export function mergeExampleIntoLinkData(linkData, example) {
  if (!example?.url) {
    const next = { ...linkData };
    delete next.exampleImageUrl;
    delete next.exampleImageId;
    delete next.exampleImageName;
    delete next.exampleSource;
    return next;
  }
  return {
    ...linkData,
    exampleImageUrl: example.url,
    exampleImageId: example.id,
    exampleImageName: example.name,
    exampleSource: example.source,
  };
}

const STAMP_PARAM_FIELD_LABELS = {
  fillMode: '印花填充模式',
  objectPosition: '对齐方式',
  stroke: '描边',
  strokeWidth: '描边大小',
  strokeColor: '描边颜色',
  stampRotation: '印花旋转',
  opacity: '不透明度',
  brightness: '图片亮度',
  wrinkleFit: '褶皱贴合',
  wrinkleIntensity: '褶皱强度',
  ambientLight: '环境光照',
  highlightIntensity: '高光强度',
  shadowIntensity: '阴影强度',
  shadow: '阴影',
  shadowDiffusion: '阴影扩散',
  shadowOffsetX: '阴影横向偏移',
  shadowOffsetY: '阴影竖向偏移',
  shadowColor: '阴影颜色',
};

const STAMP_PARAM_OPTION_MAP = {
  fillMode: STAMP_FILL_MODE_OPTIONS,
  objectPosition: STAMP_OBJECT_POSITION_OPTIONS,
  stampRotation: STAMP_ROTATION_OPTIONS,
  aspectRatio: STAMP_ASPECT_RATIO_OPTIONS,
};

function resolveOptionLabel(key, value) {
  const options = STAMP_PARAM_OPTION_MAP[key];
  if (!options) return String(value ?? '');
  return options.find((item) => item.value === value)?.label || String(value ?? '');
}

/** 印花参数变更 → 历史记录文案 */
export function formatStampParamHistoryLabel(key, value) {
  const field = STAMP_PARAM_FIELD_LABELS[key];
  if (!field) return '调整印花参数';

  const options = STAMP_PARAM_OPTION_MAP[key];
  if (options) {
    return `调整${field}：${resolveOptionLabel(key, value)}`;
  }

  if (typeof value === 'boolean') {
    return value ? `开启${field}` : `关闭${field}`;
  }

  if (['opacity', 'brightness', 'highlightIntensity', 'shadowIntensity', 'wrinkleIntensity'].includes(key)) {
    return `调整${field}：${value}%`;
  }

  if (['shadowDiffusion', 'shadowOffsetX', 'shadowOffsetY', 'strokeWidth'].includes(key)) {
    return `调整${field}：${value}`;
  }

  if (['shadowColor', 'strokeColor'].includes(key)) {
    return `调整${field}：${value}`;
  }

  return `调整${field}`;
}

export function formatStampAspectRatioHistoryLabel(aspectRatio) {
  return `调整区域比例：${resolveOptionLabel('aspectRatio', aspectRatio)}`;
}

export function formatStampSizeHistoryLabel(dimension, value) {
  const num = Math.max(1, Math.round(Number(value) || 0));
  return dimension === 'width'
    ? `调整印花区域宽度：${num}`
    : `调整印花区域高度：${num}`;
}

export function formatStampExampleHistoryLabel(example, action = 'set') {
  if (action === 'clear') return '清除参考印花';
  const name = example?.name ? `：${example.name}` : '';
  return `设置参考印花${name}`;
}
